import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  CartDetail,
  CartItem,
  DeliveryDetailsFormFields,
  GiftCardStyle
} from 'types';

export type GiftCardsShopStore = {
  isContinueShopping: boolean;
  isRedirectWithoutModal: boolean;
  selectedGiftCardStyle: GiftCardStyle | null;
  selectedGiftCardAmount: number;
  cartDetail: CartDetail | null;
  currentOrderItemId: number;
  giftCardsOrderList: CartItem[];
  downloadLink: string;
  deliveryDetails: Record<number, DeliveryDetailsFormFields>;
  isPurchased: boolean;
};

const initialState: GiftCardsShopStore = {
  isContinueShopping: false,
  isRedirectWithoutModal: false,
  selectedGiftCardStyle: null,
  selectedGiftCardAmount: 0,
  deliveryDetails: {},
  cartDetail: null,
  currentOrderItemId: 0,
  giftCardsOrderList: [],
  downloadLink: '',
  isPurchased: false
};

export const giftCardsSlice = createSlice({
  name: 'giftCardsShop',
  initialState,
  reducers: {
    setIsContinueShopping: (state, action: PayloadAction<boolean>) => {
      state.isContinueShopping = action.payload;
    },
    setIsRedirectWithoutModal: (state, action: PayloadAction<boolean>) => {
      state.isRedirectWithoutModal = action.payload;
    },
    setSelectedGiftCardStyle: (
      state,
      action: PayloadAction<GiftCardStyle | null>
    ) => {
      state.selectedGiftCardStyle = action.payload;
    },
    setSelectedGiftCardAmount: (state, action: PayloadAction<number>) => {
      state.selectedGiftCardAmount = action.payload;
    },
    setGiftCardsOrderList: (state, action: PayloadAction<CartItem[]>) => {
      state.giftCardsOrderList = action.payload;
    },
    setCartDetail: (state, action: PayloadAction<CartDetail>) => {
      state.cartDetail = action.payload;
    },
    setCurrentOrderItemId: (state, action: PayloadAction<number>) => {
      state.currentOrderItemId = action.payload;
    },
    setDeliveryDetails: (
      state,
      action: PayloadAction<{
        itemId: number;
        details: DeliveryDetailsFormFields;
      }>
    ) => {
      const { itemId, details } = action.payload;
      state.deliveryDetails[itemId] = details;
    },
    setDownloadLink: (state, action: PayloadAction<string>) => {
      state.downloadLink = action.payload;
    },
    setIsPurchased: (state, action: PayloadAction<boolean>) => {
      state.isPurchased = action.payload;
    },
    resetForm: () => initialState,
    resetFormTo3ndStep: (state) => {
      // we need it to have some form basic value when guest register/login as user and avoid persist all other info
      const {
        selectedGiftCardStyle,
        selectedGiftCardAmount,
        isContinueShopping,
        isRedirectWithoutModal,
        currentOrderItemId,
        deliveryDetails
      } = state;
      Object.assign(state, initialState);
      state.isContinueShopping = isContinueShopping;
      state.isRedirectWithoutModal = isRedirectWithoutModal;
      state.currentOrderItemId = currentOrderItemId;
      state.selectedGiftCardStyle = selectedGiftCardStyle;
      state.selectedGiftCardAmount = selectedGiftCardAmount;
      state.deliveryDetails = deliveryDetails;
    }
  }
});

export const {
  setSelectedGiftCardStyle,
  setSelectedGiftCardAmount,
  setIsRedirectWithoutModal,
  setCartDetail,
  resetForm,
  resetFormTo3ndStep,
  setDeliveryDetails,
  setCurrentOrderItemId,
  setDownloadLink,
  setGiftCardsOrderList,
  setIsContinueShopping,
  setIsPurchased
} = giftCardsSlice.actions;

export default giftCardsSlice.reducer;
